/*******************************************************************************
 * @ngdoc  service
 * @module csm.ui.validators
 * @name   csm.ui.validators.service:CSMValidatorService
 * @description
 * Input field validator services.
 *******************************************************************************/

/* @ngInject */
export function csmValidatorService($injector) {
  var $svcs = { $svc: this };
  this.makeDirective = $injector.invoke(makeDirective, null, $svcs);
  this.BaseValidator = $injector.invoke(BaseValidator, null, $svcs);
  this.UIGrid = $injector.invoke(UIGrid, null, $svcs);
}

/*******************************************************************************
 * @ngdoc    overview
 * @name     csm.ui.validators.run:CSMValidatorService
 * @description
 * Initializes the validator service.
 *******************************************************************************/
/* @ngInject */
export function csmValidatorServiceRun($templateCache, CSMValidatorService) {
  $templateCache.put(
    'csm/cellTooltipValidator',
    CSMValidatorService.UIGrid.makeCellTemplate('{{COL_FIELD CUSTOM_FILTERS}}')
  );
}

/*******************************************************************************
* $svc.makeDirective
/**
* @ngdoc    method
* @name     makeDirective
* @methodOf csm.ui.validators.service:CSMValidatorService
* @param    {object} args Properties to augment directive specification:
*   * `doValidate`     - Method to perform validation. Takes input value as 
*                        parameter and returns an error message if validation 
*                        fails. `null`=validation successful.
*   * `link`           - Linker function, optional. Validator reference passed 
*                        in additional `val` parameter.
*   * `require`        - Required controllers, optional. Must be an object.
*   * `titleAttribute` - Name of element attribute containing the error message 
*                        popup title. Default=directive name.
* @description
* Creates a validator attribute directive that will evaluate these attributes
* in addition to the `titleAttribute`:
*******************************************************************************/
/* @ngInject */
function makeDirective($svc, TextStrings) {
  return function (args) {
    var args = args || {};
    return {
      restrict: 'A',
      require: Object.assign(
        { validators: '?^^csmValidators' },
        args.require || {}
      ),
      link: function (scope, elem, attr, ctrl) {
        if (ctrl.validators) {
          /*------------------------------------------------*/
          /* Create the validator and add to csmValidators. */
          /*------------------------------------------------*/
          var val = new $svc.BaseValidator(
            elem,
            attr,
            args.titleAttribute || this.name,
            args.doValidate
          );
          ctrl.validators.addValidator(val);
          scope.$on('$destroy', function () {
            ctrl.validators.removeValidator(val);
          });

          /*------------------------------------------------------*/
          /* Call linker passed in, if any, and supply validator. */
          /*------------------------------------------------------*/
          if (angular.isFunction(args.link))
            args.link(scope, elem, attr, ctrl, val);
        } else
          console.warn(
            TextStrings.toKabobCase(this.name) + ':',
            "'csm-validators' not found on any parent elements."
          );
      }
    };
  };
}

/*****************************************************************************
 * $svc.BaseValidator */
/**
 * @ngdoc    object
 * @module   csm.ui.validators
 * @name     csm.ui.validators.type:CSMValidatorService#BaseValidator
 *
 * @constructor
 * @param {object}   elem           jqlite/jQuery Element to monitor
 * @param {object}   attr           Angular attributes object
 * @param {string}   titleAttribute Attribute property name to obtain error title
 * @param {function} validator      Validator method, replaces `doValidate`.
 *
 * @description
 * This class creates an object that provides validation methods to be update
 * the provided element upon success/fail. The attr parameter is expected to
 * be the angular attribute object from the directive link function.
 *
 * This class can expect the disableValidator attribute which when 'true' will
 * skip validation on the element.
 *******************************************************************************/
/* @ngInject */ function BaseValidator(toastr, Tools) {
  var CLASS_INVALID = 'csmInvalidInput';
  function validator(elem, attr, titleAttribute, validator) {
    this.attributes = attr; // Validator element attributes.
    this.element = elem; // Validator element.
    this.errorElement = null; // Element to surround with error border.
    this.notice = null; // `toastr` service notice identifier.
    this.doValidate = validator;
    this.titleAttribute = titleAttribute;
  }

  validator.prototype = {
    /***************************************************************************
     * doValidate */
    /**
     * @ngdoc    function
     * @name     doValidate
     * @methodOf csm.ui.validators.type:CSMValidatorService#BaseValidator
     * @param    {object} value  Input value to validate.
     * @return   {string}        Error message, null=validation successful.
     * @description
     * Standard validation method. Checks value of `csm-required` and verifies
     * a value has been supplied. This method is overridden by directives
     * created with `makeDirective` and may call this method directly.
     ***************************************************************************/
    doValidate: function (value) {
      var isRequired = this.attributes.csmRequired != 'false';
      return isRequired && Tools.isEmpty(value) ? 'Value is required' : null;
    },

    /***************************************************************************
     * reset */
    /**
     * @ngdoc    function
     * @name     reset
     * @methodOf csm.ui.validators.type:CSMValidatorService#BaseValidator
     *
     * @description
     * Clears any validation error messages and removes error class from error
     * element.
     ***************************************************************************/
    reset: function () {
      (this.errorElement || this.element).removeClass(CLASS_INVALID);

      if (this.notice) {
        toastr.clear(this.notice);
        this.notice = null;
      }
    },

    /***************************************************************************
     * validate */
    /**
     * @ngdoc    function
     * @name     validate
     * @methodOf csm.ui.validators.type:CSMValidatorService#BaseValidator
     * @return   {boolean} `true`=validation successful.
     * @description
     * Performs data validation on the element if the validation has not been
     * disabled. The value to check will come from the evaluated ngModel property
     * on the scope or from the val expression of the element.
     ***************************************************************************/
    validate: function () {
      var errorMessage = null;
      this.reset();

      /*---------------------------------------------*/
      /* Check for validation errors if not disabled */
      /*---------------------------------------------*/
      if (this.attributes.disableValidator != 'true') {
        var hasModel = this.attributes.hasOwnProperty('ngModel');
        var modelValue = hasModel
          ? this.element.scope().$eval(this.attributes.ngModel)
          : null;

        /*-----------------------------------------------------------------*/
        /* If value is a function, assume its a getter and update property */
        /*-----------------------------------------------------------------*/
        if (angular.isFunction(modelValue)) modelValue = modelValue();

        var value = hasModel ? modelValue : this.element.val();
        errorMessage = this.doValidate(value);
      }

      /*---------------------------------------------------*/
      /* Show validation error using a sticky notification */
      /*---------------------------------------------------*/
      if (errorMessage) {
        (this.errorElement || this.element).addClass(CLASS_INVALID);
        this.notice = toastr.error(
          errorMessage,
          this.attributes[this.titleAttribute],
          { timeOut: 0 }
        );
      }

      return !errorMessage;
    }
  };

  return validator;
}

/*******************************************************************************
* $svc.UIGrid
/**
* @ngdoc       service
* @name        csm.ui.validators.service:CSMValidatorService#UIGrid
* @description Services for validators in `angular-ui-grid`.
*******************************************************************************/
/* @ngInject */ function UIGrid(
  $svc,
  $injector,
  $q,
  $sce,
  $templateCache,
  toastr
) {
  var errorsTooltipName = function (colDef) {
    return '$$errorsTooltip' + colDef.name;
  };
  var uiGridValidateService = $injector.has('uiGridValidateService')
    ? $injector.get('uiGridValidateService')
    : null;
  var uiGridSvc = {};

  /*****************************************************************************
   * $svc.UIGrid.disableValidator */
  /**
   * @ngdoc    function
   * @name     disableValidator
   * @methodOf csm.ui.validators.service:CSMValidatorService#UIGrid
   * @param    {object} rowEntity  Grid row entity object.
   * @param    {object} colDef     Grid column definition.
   * @description
   * Called by validator factories created by validator directives. Returns `true`
   * if the column is not editable according to `colDef` settings `enableCellEdit`
   * and `cellEditableCondition`.
   *
   * In addition, `colDef` may define a `disableValidator(rowEntity, colDef)`
   * function, returning `true` to skip validation. This function will be called
   * if validation is not already disabled by `colDef` settings.
   *****************************************************************************/
  uiGridSvc.disableValidator = function (rowEntity, colDef) {
    if (!colDef.enableCellEdit) return true;

    if (colDef.hasOwnProperty('cellEditableCondition'))
      if (
        angular.isFunction(colDef.cellEditableCondition) &&
        !colDef.cellEditableCondition({
          row: { entity: rowEntity },
          col: { colDef: colDef }
        })
      )
        return true;
      else if (!colDef.cellEditableCondition) return true;

    if (
      angular.isFunction(colDef.disableValidator) &&
      colDef.disableValidator(rowEntity, colDef)
    )
      return true;

    return false;
  };

  /*****************************************************************************
   * $svc.UIGrid.makeCellTemplate */
  /**
   * @ngdoc    function
   * @name     makeCellTemplate
   * @methodOf csm.ui.validators.service:CSMValidatorService#UIGrid
   * @param    {string} element  Input element.  Grid row entity object.
   * @return   {string} Template for use as `cellTemplate`.
   * @description
   * Returns an HTML `<div>` template for use with `cellTemplate` in column
   * definitions for cells that require validation. Calls to `Validator.validate()`
   * will create error tooltips displayed on mouseenter for cells that fail
   * validation. `element` is transcluded into the `<div>`.
   *****************************************************************************/
  uiGridSvc.makeCellTemplate = function (control) {
    return (
      '<div class                  = "ui-grid-cell-contents" ' +
      'ng-class               = "{csmInvalidInput:grid.validate.isInvalid(row.entity,col.colDef)}" ' +
      'uib-tooltip-html       = "grid.validate.getErrorsTooltip(row.entity,col.colDef)" ' +
      'tooltip-append-to-body = "true" ' +
      'tooltip-enable         = "grid.validate.isInvalid(row.entity,col.colDef)" ' +
      'tooltip-class          = "csmInvalidTooltip" ' +
      'tooltip-placement      = "top-left">' +
      control +
      '</div>'
    );
  };

  /*****************************************************************************
   * $svc.UIGrid.setValidator */
  /**
   * @ngdoc    function
   * @name     setValidator
   * @methodOf csm.ui.validators.service:CSMValidatorService#UIGrid
   * @param    {string}   name              Validator name.
   * @param    {function} validatorFactory  Returns validator function.
   * @param    {function} messageFunction   Returns validator error message.
   * @description
   * Called by validator directives. Sugar for adding a validator to the grid
   * validator factories. This calls `uiGridValidateService.setValidator()` only
   * if `uiGridValidateService` is loaded.
   *
   * Note: The documentation for `setValidator` describes `validatorFactory` as
   * taking `argument` as a parameter and returning a function with a signature
   * of `(newValue, oldValue, rowEntity, colDef)`. This is incorrect. This
   * function is called by uiGridValidateService.runValidators` which passes
   * `(oldValue, newValue, rowEntity, colDef)`.
   *****************************************************************************/
  uiGridSvc.setValidator = function (name, validatorFactory, messageFunction) {
    if (!!uiGridValidateService)
      uiGridValidateService.setValidator(
        name,
        validatorFactory,
        messageFunction
      );
  };

  /*****************************************************************************
   * $svc.UIGrid.Validators */
  /**
   * @ngdoc    object
   * @module   csm.ui.validators
   * @name     csm.ui.validators.type:CSMValidatorService#UIGrid.Validators
   *
   * @constructor
   * @param {object} api `GridApi` object passed to `onRegisterApi`.
   *
   * @description
   * For use with the `angular-ui-grid` to perform column value validations using
   * using built-in, after edit validation mechanism. Constructor monkey-patches
   * `uiGridValidateService` to initialize the default validators and add the
   * `getErrorsTooltip(rowEntity, colDef)` method for use with `uib-tooltip-html`.
   *****************************************************************************/
  uiGridSvc.Validators = function (api) {
    if (!!uiGridValidateService) {
      this.api = api;

      /*----------------------------------------------------------------------*/
      /* Add default validators to validator factories. This is done when     */
      /* `ui-grid-validate` directive is used but doing it twice is harmless. */
      /*----------------------------------------------------------------------*/
      uiGridValidateService.createDefaultValidators();

      /*--------------------------------------------*/
      /* Add method for use with `uib-tooltip-html` */
      /* for HTML formatted error tooltips.         */
      /*--------------------------------------------*/
      uiGridValidateService.getErrorsTooltip = function (rowEntity, colDef) {
        var tipname = errorsTooltipName(colDef);
        var tooltip =
          rowEntity[tipname] ||
          (rowEntity[tipname] = $sce.trustAsHtml(
            this.getErrorMessages(rowEntity, colDef).join('<br/>')
          ));
        return tooltip;
      };

      /*-----------------------------------------------------------------*/
      /* Add validate services to grid, if necessary. 'ui-grid-validate' */
      /* will do this but will also turn on end-edit validation.         */
      /*-----------------------------------------------------------------*/
      if (!api.grid.validate) api.grid.validate = uiGridValidateService;
    } else
      throw new Error(
        "CSMValidatorService.UIGrid.Validators: Check module dependencies: 'ui.grid.validate' is required."
      );
  };

  uiGridSvc.Validators.prototype = {
    /*************************************************************************
     * clearErrors */
    /**
     * @ngdoc    function
     * @name     clearErrors
     * @methodOf csm.ui.validators.type:CSMValidators#UIGrid
     * @description
     * Clears validation errors in all rows.
     ***************************************************************************/
    clearErrors: function () {
      var grid = this.api.grid;
      grid.rows.forEach(function (row) {
        grid.columns.forEach(function (col) {
          uiGridValidateService.setValid(row.entity, col.colDef);
          delete row.entity[errorsTooltipName(col.colDef)];
        });
      });
    },

    /*************************************************************************
     * clearNotice */
    /**
     * @ngdoc    function
     * @name     clearErrors
     * @methodOf csm.ui.validators.type:CSMValidators#UIGrid
     * @description
     * Removes error balloon.
     ***************************************************************************/
    clearNotice: function () {
      if (this.notice) {
        toastr.clear(this.notice);
        this.notice = null;
      }
    },

    /*************************************************************************
     * reset */
    /**
     * @ngdoc    function
     * @name     reset
     * @methodOf csm.ui.validators.type:CSMValidators#GridValidators
     * @description
     * Calls `clearErrors` and `clearNotice`.
     ***************************************************************************/
    reset: function () {
      this.clearErrors();
      this.clearNotice();
    },

    /***************************************************************************
     * validate */
    /**
     * @ngdoc    function
     * @name     validate
     * @methodOf csm.ui.validators.type:CSMValidators#GridValidators
     * @param    {string}  title  Error message title. Default='Errors'.
     * @return   {promise} Resolves on success, rejects with an array of
     *                     {row,col} objects for each cell that failed validation.
     * @description
     * Performs data validation on all rows. Patches into `angular-ui-grid`
     * validation mechanism that validates as cell values are changed. Performs
     * validation on all cells and shows error message balloon for any errors
     * found. Only one balloon to include all errors with a counter for duplidates.
     ***************************************************************************/
    validate: function (title) {
      var self = this;
      var defer = $q.defer();
      var grid = self.api.grid;

      /*--------------------*/
      /* Validate all rows. */
      /*--------------------*/
      var promises = [];
      grid.rows.forEach(function (row) {
        grid.columns.forEach(function (col) {
          /*---------------------------------------------------------------------*/
          /* `runValidators` performs validation and returns resolved promise.   */
          /* Validation will not be attempted if `newValue` == `oldValue' so     */
          /* pass '{}' as `oldValue`. `isInvalid` to check if validation failed. */
          /*---------------------------------------------------------------------*/
          promises.push(
            uiGridValidateService
              .runValidators(
                row.entity,
                col.colDef,
                grid.getCellValue(row, col),
                {}
              )
              .then(function () {
                /*--------------------------------------*/
                /* Resolve with null if success and     */
                /* with {row,col} if validation failed. */
                /*--------------------------------------*/
                return $q.resolve(
                  uiGridValidateService.isInvalid(row.entity, col)
                    ? { row: row, col: col }
                    : null
                );
              })
          );
        });
      });

      /*---------------------------------------------------*/
      /* Wait for all validations and resolve if all       */
      /* succeeded. Else, show message balloon and reject. */
      /*---------------------------------------------------*/
      $q.all(promises).then(function (r) {
        var errors = r.filter(function (e) {
          return !!e;
        });

        /*-----------------------*/
        /* Resolve if no errors. */
        /*-----------------------*/
        if (!errors.length) defer.resolve();
        /*----------------------------------------------------------------*/
        /* Reject with list of {row,col} objects for validation failures. */
        /*----------------------------------------------------------------*/ else {
          /*-------------------------------------------------------*/
          /* Build error message list with counter for duplicates. */
          /*-------------------------------------------------------*/
          var messages = {};
          errors.forEach(function (e) {
            uiGridValidateService
              .getErrorMessages(e.row.entity, e.col.colDef)
              .forEach(function (msg) {
                messages[msg] = (messages[msg] || 0) + 1;
              });
          });

          /*-------------------------------------------------------------------*/
          /* Show balloon with error messages.                                 */
          /*-------------------------------------------------------------------*/
          self.clearNotice();
          var message = Object.keys(messages)
            .map(function (k) {
              return k + (messages[k] > 1 ? ' (' + messages[k] + ')' : '');
            })
            .join('<br/>');
          self.notice = toastr.error(message, title || 'Errors', {
            timeOut: 0,
            allowHtml: true
          });

          /*------------------------------------*/
          /* Reject with list of error objects. */
          /*------------------------------------*/
          defer.reject(errors);
        }
      }).catch(function (e) {
        defer.reject(e);
      });

      return defer.promise;
    }
  };

  return uiGridSvc;
}
